import { useState, useEffect } from "react";

import {
  useGetExcelUploadsQuery,
  useLazyDownloadQuestionTemplateQuery,
} from "../../../slices/excelUploadApiSlice";

const UploadHistory = ({ refreshKey = 0, onViewDetails }) => {
  const [uploadTypeFilter, setUploadTypeFilter] = useState("all");
  const [quizFilter, setQuizFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");

  const {
    data: uploads = [],
    isLoading,
    error: queryError,
    refetch,
  } = useGetExcelUploadsQuery(refreshKey);

  const [downloadTemplate, { isFetching: downloadingTemplate }] =
    useLazyDownloadQuestionTemplateQuery();

  useEffect(() => {
    setError(queryError?.data?.message || queryError?.error || "");
  }, [queryError]);

  /*
  ======================================
  Quiz List
  ======================================
  */

  const quizzes = [
    ...new Map(
      uploads
        .filter((upload) => upload.quiz)
        .map((upload) => [upload.quiz._id, upload.quiz]),
    ).values(),
  ];

  /*
  ======================================
  Download Template
  ======================================
  */

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate().unwrap();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "QuizPro_Question_Upload_Template.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download Excel template.");
    }
  };

  /*
  ======================================
  Filters
  ======================================
  */

  const filteredUploads = uploads.filter((upload) => {
    const uploadTypeMatch =
      uploadTypeFilter === "all" || upload.uploadType === uploadTypeFilter;

    const quizMatch = quizFilter === "all" || upload.quiz?._id === quizFilter;

    const statusMatch =
      statusFilter === "all" || upload.status === statusFilter;

    return uploadTypeMatch && quizMatch && statusMatch;
  });

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "-";

  const getStatusClass = (status) => {
    switch (status) {
      case "completed":
        return "status-success";

      case "failed":
        return "status-error";

      case "processing":
        return "status-processing";

      default:
        return "";
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleDownloadTemplate}
        disabled={downloadingTemplate}
        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
      >
        {downloadingTemplate ? "Downloading..." : "Download Excel Template"}
      </button>

      <div className="upload-history">
        <div className="history-header">
          <h3>Excel Upload History</h3>

          <button type="button" onClick={refetch}>
            Refresh
          </button>
        </div>

        <div className="filters flex items-end gap-4 mb-4">
          <div className="flex flex-col">
            <label>Upload Type</label>

            <select
              value={uploadTypeFilter}
              onChange={(e) => setUploadTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>

              <option value="questionBank">Question Bank</option>

              <option value="quiz">Quiz</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label>Quiz</label>

            <select
              value={quizFilter}
              onChange={(e) => setQuizFilter(e.target.value)}
            >
              <option value="all">All Quizzes</option>

              {quizzes.map((quiz) => (
                <option key={quiz._id} value={quiz._id}>
                  {quiz.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label>Status</label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>

              <option value="completed">Completed</option>

              <option value="processing">Processing</option>

              <option value="failed">Failed</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => {
              setUploadTypeFilter("all");
              setQuizFilter("all");
              setStatusFilter("all");
            }}
          >
            Clear
          </button>
        </div>

        {isLoading && <p>Loading upload history...</p>}

        {error && <p className="error-message">{error}</p>}

        {!isLoading && filteredUploads.length === 0 && <p>No uploads found.</p>}

        {filteredUploads.length > 0 && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Upload Type</th>
                  <th>Quiz</th>
                  <th>Total</th>
                  <th>Success</th>
                  <th>Failed</th>
                  <th>Status</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredUploads.map((upload) => (
                  <tr key={upload._id}>
                    <td>{upload.fileName}</td>

                    <td>
                      {upload.uploadType === "questionBank"
                        ? "Question Bank"
                        : "Quiz"}
                    </td>

                    <td>
                      {upload.uploadType === "quiz"
                        ? upload.quiz?.name || "-"
                        : "-"}
                    </td>

                    <td>{upload.totalRecords ?? 0}</td>

                    <td>{upload.successfulRecords ?? 0}</td>

                    <td>{upload.failedRecords ?? 0}</td>

                    <td>
                      <span className={getStatusClass(upload.status)}>
                        {upload.status}
                      </span>
                    </td>

                    <td>{upload.uploadedBy?.name || "-"}</td>

                    <td>{formatDate(upload.createdAt)}</td>

                    <td>
                      {onViewDetails && (
                        <button
                          type="button"
                          onClick={() => onViewDetails(upload._id)}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadHistory;
